import { useState } from "react";
import { Users, Plus, Search, Mail, Phone, Building2 } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useToast } from "@/hooks/use-toast";
import { ContactModal } from "@/components/crm/ContactModal";
import { useAuth } from "@/hooks/useAuth";

interface Contact {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
  title: string | null;
  org_id: string | null;
  consent_gdpr: boolean;
  created_at: string;
  updated_at: string;
  organization?: { 
    name: string;
    website?: string | null;
    favicon_url?: string | null;
    logo_url?: string | null;
  } | null;
}

export default function ChargingContacten() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { userRole } = useAuth();
  const isChargingUser = userRole === 'charging_user';

  // Fetch contacts (exclude soft-deleted)
  const { data: contacts = [], isLoading } = useQuery({
    queryKey: ['charging-contacts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('contacts')
        .select(`
          *,
          organization:organizations(name, website, favicon_url, logo_url)
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Contact[];
    }
  });

  // Fetch organizations for modal (exclude soft-deleted)
  const { data: organizations = [] } = useQuery({
    queryKey: ['charging-organizations-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('id, name, website, favicon_url, logo_url')
        .is('deleted_at', null)
        .order('name');

      if (error) throw error;
      return data;
    }
  });

  // Delete mutation - soft delete for charging users, hard delete for CRM users
  const deleteMutation = useMutation({
    mutationFn: async (contactId: string) => {
      if (isChargingUser) {
        // Soft delete for charging users
        const { error } = await supabase
          .from('contacts')
          .update({ deleted_at: new Date().toISOString() })
          .eq('id', contactId);
        if (error) throw error;
      } else {
        // Hard delete for CRM users
        const { error } = await supabase
          .from('contacts')
          .delete()
          .eq('id', contactId);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['charging-contacts'] });
      toast({
        title: isChargingUser ? "Contact verborgen" : "Contact verwijderd",
        description: isChargingUser 
          ? "Het contact is verborgen. CRM gebruikers kunnen dit nog zien." 
          : "Het contact is permanent verwijderd."
      });
    },
    onError: (error) => {
      toast({
        title: "Fout bij verwijderen",
        description: error.message,
        variant: "destructive"
      });
    }
  });

  // Filter contacts
  const filteredContacts = contacts.filter(contact => {
    const fullName = `${contact.first_name || ''} ${contact.last_name || ''}`.toLowerCase();
    const email = (contact.email || '').toLowerCase();
    const orgName = (contact.organization?.name || '').toLowerCase();
    const search = searchTerm.toLowerCase();
    
    return fullName.includes(search) || email.includes(search) || orgName.includes(search);
  });

  const handleAdd = () => {
    setSelectedContact(null);
    setIsModalOpen(true);
  };

  const handleEdit = (contact: Contact) => {
    setSelectedContact(contact);
    setIsModalOpen(true);
  };

  const handleDelete = async (contactId: string) => {
    const confirmMessage = isChargingUser 
      ? 'Weet je zeker dat je dit contact wilt verbergen? CRM gebruikers kunnen dit nog steeds zien.'
      : 'Weet je zeker dat je dit contact permanent wilt verwijderen?';
    if (confirm(confirmMessage)) {
      deleteMutation.mutate(contactId);
    }
  };

  const getInitials = (contact: Contact) => {
    const first = contact.first_name?.[0] || '';
    const last = contact.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  const getOrgFavicon = (org: Contact['organization']) => {
    if (!org) return null;
    
    // Prefer logo_url, then favicon_url, then Google favicon service
    if (org.logo_url) return org.logo_url;
    if (org.favicon_url) return org.favicon_url;
    if (org.website) {
      const domain = org.website.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
      return `https://www.google.com/s2/favicons?sz=32&domain=${domain}`;
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="container mx-auto p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/4"></div>
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-48 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Users className="h-8 w-8" />
              Contacten
            </h1>
            <p className="text-muted-foreground mt-2">
              Beheer contactpersonen zichtbaar voor Greenvision Charging
            </p>
          </div>
          <Button onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Nieuw Contact
          </Button>
        </div>
      </div>

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Zoek op naam, email of organisatie..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 max-w-md"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredContacts.map((contact) => (
          <Card key={contact.id} className="hover:shadow-lg transition-shadow">
            <CardHeader>
              <div className="flex items-start gap-3">
                <Avatar className="h-12 w-12">
                  {(() => {
                    const favicon = getOrgFavicon(contact.organization);
                    return favicon ? (
                      <AvatarImage 
                        src={favicon} 
                        alt="" 
                        className="object-contain"
                      />
                    ) : null;
                  })()}
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {getInitials(contact)}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <CardTitle className="text-lg truncate">
                    {contact.first_name} {contact.last_name}
                  </CardTitle>
                  {contact.title && (
                    <p className="text-sm text-muted-foreground truncate">
                      {contact.title}
                    </p>
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {contact.organization && (
                <div className="flex items-center gap-2 text-sm">
                  {(() => {
                    const favicon = getOrgFavicon(contact.organization);
                    return favicon ? (
                      <img 
                        src={favicon}
                        alt=""
                        className="h-4 w-4 object-contain flex-shrink-0 rounded-sm"
                        onError={(e) => { 
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.nextElementSibling?.classList.remove('hidden');
                        }}
                      />
                    ) : null;
                  })()}
                  <Building2 className={`h-4 w-4 text-muted-foreground flex-shrink-0 ${getOrgFavicon(contact.organization) ? 'hidden' : ''}`} />
                  <span className="text-muted-foreground truncate">
                    {contact.organization.name}
                  </span>
                </div>
              )}

              {contact.email && (
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <a 
                    href={`mailto:${contact.email}`} 
                    className="text-primary hover:underline truncate"
                  >
                    {contact.email}
                  </a>
                </div>
              )}

              {contact.phone && (
                <div className="flex items-center gap-2 text-sm">
                  <Phone className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <a 
                    href={`tel:${contact.phone}`} 
                    className="text-primary hover:underline"
                  >
                    {contact.phone}
                  </a>
                </div>
              )}

              {contact.consent_gdpr && (
                <Badge variant="outline" className="text-xs">
                  GDPR Toestemming
                </Badge>
              )}

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEdit(contact)}
                  className="flex-1"
                >
                  Bewerken
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDelete(contact.id)}
                  className="flex-1"
                >
                  {isChargingUser ? 'Verbergen' : 'Verwijderen'}
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredContacts.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">Geen contacten gevonden</h2>
          <p className="text-muted-foreground mb-4">
            {searchTerm ? 'Probeer een andere zoekterm' : 'Voeg je eerste contact toe'}
          </p>
          {!searchTerm && (
            <Button onClick={handleAdd}>
              <Plus className="h-4 w-4 mr-2" />
              Nieuw Contact
            </Button>
          )}
        </div>
      )}

      <ContactModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedContact(null);
        }}
        contact={selectedContact}
        onSave={() => {
          queryClient.invalidateQueries({ queryKey: ['charging-contacts'] });
        }}
        organizations={organizations}
      />
    </div>
  );
}
